import { BaseApiService } from './BaseApiService';
import { WeightCalculatorResult } from '../../components/weight/WeightCalculator';

export interface UserGoal {
  id: string;
  userId: string;
  targetWeight: number;
  currentWeight: number;
  height: number;
  age: number;
  gender: 'male' | 'female';
  timeframeMonths: number;
  activityLevel: number;
  dailyCalories: number;
  dailyProtein: number;
  dailyCarbs: number;
  dailyFats: number;
  ratePerWeek: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserGoalRequest {
  targetWeight: number;
  currentWeight: number;
  height: number;
  age: number;
  gender: 'male' | 'female';
  timeframeMonths: number;
  activityLevel: number;
  dailyCalories: number;
  dailyProtein: number;
  dailyCarbs: number;
  dailyFats: number;
  ratePerWeek: number;
}

export class UserGoalsService extends BaseApiService {
  private static instance: UserGoalsService;
  private goalCache: UserGoal | null = null;

  static getInstance(): UserGoalsService {
    if (!UserGoalsService.instance) {
      UserGoalsService.instance = new UserGoalsService();
    }
    return UserGoalsService.instance;
  }

  async getGoal(): Promise<UserGoal | null> {
    if (this.goalCache) {
      return this.goalCache;
    }

    try {
      const response = await this.get<UserGoal>('/user-goals');
      this.goalCache = response;
      return response;
    } catch (error: any) {
      if (error.message?.includes('No goal found')) {
        return null;
      }
      throw error;
    }
  }

  async saveGoal(calculatorResult: WeightCalculatorResult, inputs: {
    targetWeight: number;
    currentWeight: number;
    height: number;
    age: number;
    gender: 'male' | 'female';
    timeframeMonths: number;
    activityLevel: number;
  }): Promise<UserGoal> {
    const request: CreateUserGoalRequest = {
      targetWeight: inputs.targetWeight,
      currentWeight: inputs.currentWeight,
      height: inputs.height,
      age: inputs.age,
      gender: inputs.gender,
      timeframeMonths: inputs.timeframeMonths,
      activityLevel: inputs.activityLevel,
      dailyCalories: calculatorResult.calories,
      dailyProtein: calculatorResult.protein,
      dailyCarbs: calculatorResult.carbs,
      dailyFats: calculatorResult.fats,
      ratePerWeek: calculatorResult.ratePerWeek,
    };

    const response = await this.post<UserGoal>('/user-goals', request);
    this.goalCache = response;
    return response;
  }

  async deleteGoal(): Promise<void> {
    await this.delete('/user-goals');
    this.goalCache = null;
  }

  clearCache(): void {
    this.goalCache = null;
  }
}

export const userGoalsService = UserGoalsService.getInstance();
